/**
 * parallax — a distant procedural-scenery silhouette band (mountains / spires /
 * columns / cone / dunes / ridge) that scrolls SLOWER than the world for depth.
 * Drawn as one wide IGraphics fill at Z_LAYERS.BACKGROUND (behind the atmosphere
 * + everything), repositioned each frame at the layer's parallax `speed`.
 *
 * Geometry is the pure `silhouettePoints` (seeded, stable skyline). The band is
 * drawn ~3× the view width so it covers a generous pan on the showcase maps
 * (not infinite-scroll). Vertical position is fixed at mount (these maps pan
 * mostly horizontally).
 *
 * Coil art pack (Lane D): a layer may carry an authored `svg` silhouette set
 * (`ParallaxSvgSpec`) — a repeating terrain tile + optional one-shot landmark
 * (the Coil Light). When present, the pure `svgSilhouetteFills` resolves the
 * SVG paths into polygon fills drawn instead of the procedural profile;
 * without it every plane falls back to `silhouettePoints` unchanged.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { createGraphics } from '../../../rendering/utils/Graphics';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { silhouettePoints, swayOffset } from './parallaxConfig';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { svgTileFills, svgFeatureFills } from '../silhouettes/svgSilhouette';

/**
 * Band TOP world-Y (scene-schema). The silhouette is drawn locally over
 * `[0..height]` and positioned at this `baseY`, so the band occupies
 * `[baseY, baseY + height]`. We anchor the band's BOTTOM to `horizonY` (the
 * terrain-fixed sky/scenery seam) and let `layer.y` lift it ABOVE the horizon:
 *   baseY + height + lift = horizonY  →  baseY = horizonY - height - lift.
 * Pure (no Pixi) so the anchor math is unit-testable.
 */
export function parallaxBaseY(horizonY: number, height: number, lift = 0): number {
  return horizonY - height - lift;
}

/**
 * Foreground band TOP world-Y. A foreground occluder anchors to the terrain
 * GROUND (scene.groundY) with its silhouette TIPS rising `tipRise` px above it;
 * the band body fills DOWN from there (height) past the bottom of the view. So
 * the silhouette pokes up around the player's feet and occludes the immediate
 * foreground. Pure (no Pixi) — unit-testable.
 */
export function parallaxForegroundBaseY(groundY: number, tipRise = 0): number {
  return groundY - tipRise;
}

/**
 * Ceiling band TOP world-Y. A ceiling band anchors to the scene sky/cavern roof
 * (scene.skyTopY) with `drop` lowering the band DOWN into the scene (stalactites,
 * hanging roots, cave vaults). Pure (no Pixi) — unit-testable.
 */
export function parallaxCeilingBaseY(skyTopY: number, drop = 0): number {
  return skyTopY + drop;
}

/**
 * Compute the required parallax band width to guarantee continuous horizontal
 * coverage across the entire level traversal without edge runout or void gaps.
 */
export function parallaxBandWidth(
  viewW: number,
  margin: number,
  speed: number,
  boundsWidth = 0,
): number {
  const maxLevelTravel = Math.max(0, Math.max(boundsWidth, viewW * 3) - viewW);
  const maxScrollShift = Math.ceil(Math.max(0, speed) * maxLevelTravel);
  return viewW + margin * 2 + maxScrollShift + 200;
}

const buildParallax: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'parallax') return null;
  const { region } = ctx;
  const viewW = region.x1 - region.x0;
  const margin = viewW; // a view-width of slack on each side
  const width = parallaxBandWidth(viewW, margin, layer.speed, ctx.bounds?.width);
  // Background: band BOTTOM on the scene horizon, `layer.y` lifts it above.
  // Foreground: band anchors to the scene GROUND, tips rising `layer.y` above it.
  // Ceiling: band anchors to scene skyTopY, dropping `layer.y` down from the vault roof.
  const baseY = layer.plane === 'foreground'
    ? parallaxForegroundBaseY(ctx.scene.groundY, layer.y ?? 0)
    : layer.plane === 'ceiling'
      ? parallaxCeilingBaseY(ctx.scene.skyTopY, layer.y ?? 0)
      : parallaxBaseY(ctx.scene.horizonY, layer.height, layer.y ?? 0);

  // LOCAL silhouette [0..width] × [0..height]; the gfx position places it.
  // Authored `svg` silhouettes (Coil art pack) win; otherwise the procedural
  // profile (`detail` densifies the skyline, granular, PX1). The container's z
  // (the layer's `depth` override) stacks this plane. A one-shot landmark (the
  // Coil Light) draws into its OWN gfx — created before/after the tile gfx so
  // sibling paint order realises `feature.order` (behind vs in front).
  const band = { width, height: layer.height, viewW, margin };
  const base = { color: layer.color, alpha: layer.alpha ?? 0.4 };
  const feature = layer.svg?.feature;
  const makeGfx = () => createGraphics(null, { depth: Z_LAYERS.BACKGROUND, parent: ctx.layerContainer });
  let featureGfx: ReturnType<typeof makeGfx> | null = null;
  if (feature && feature.order === 'behindTile') featureGfx = makeGfx();
  const gfx = makeGfx();
  if (feature && feature.order !== 'behindTile') featureGfx = makeGfx();
  if (layer.svg) {
    const tileFills = svgTileFills(layer.svg.tile, band, base);
    const shouldOcclude = layer.occludeCelestial === true;
    const occludeColor = layer.occludeColor ?? ctx.scene.ambientTint ?? 0x141824;
    if (shouldOcclude) {
      for (const f of tileFills) gfx.fillPolygon(f.points, occludeColor, 1.0);
    }
    for (const f of tileFills) gfx.fillPolygon(f.points, f.color, f.alpha);
  } else {
    const pts = silhouettePoints(layer.shape, 0, 0, width, layer.height, ctx.seed, layer.detail, layer.plane);
    const shouldOcclude = layer.occludeCelestial === true;
    const occludeColor = layer.occludeColor ?? ctx.scene.ambientTint ?? 0x141824;
    if (shouldOcclude) gfx.fillPolygon(pts, occludeColor, 1.0);
    gfx.fillPolygon(pts, layer.color, layer.alpha ?? 0.4);
  }

  // A world child at x appears at screen (x - cameraLeft). Offsetting by
  // cameraLeft*(1-speed) makes the scenery scroll at `speed` × the camera
  // (speed 0 = static distant, 1 = moves with the world). An optional `sway` adds
  // a slow sine drift so the plane breathes sideways (PX1); reduced motion drops the sway and
  // keeps the scroll, which follows the camera.
  //
  // The landmark bakes its anchor from the FIRST tick with a live camera (the
  // camera isn't placed yet when the atmosphere mounts): featureOffsetPx =
  // cameraLeft×speed puts it at `anchorX` of the view the player actually sees,
  // and from then on it parallax-scrolls with the plane like any world fixture.
  const sway = getMotionPrefs().reduceMotion ? undefined : layer.sway;
  let elapsed = 0;
  let featureDrawn = false;
  const reposition = (dtMs = 0) => {
    elapsed += dtMs;
    const v = ctx.getCameraView();
    if (!v) return;
    const cameraLeft = v.centerX - v.width / 2;
    if (feature && featureGfx && !featureDrawn) {
      featureDrawn = true;
      const fills = svgFeatureFills(feature, { ...band, featureOffsetPx: cameraLeft * layer.speed }, base);
      for (const f of fills) featureGfx.fillPolygon(f.points, f.color, f.alpha);
    }
    const x = cameraLeft * (1 - layer.speed) - margin + swayOffset(elapsed, sway);
    gfx.setPosition(x, baseY);
    if (featureGfx) featureGfx.setPosition(x, baseY);
  };
  reposition();
  const unsub = ctx.addTick(reposition);

  return makeContentHandle({
    onDestroy: () => {
      unsub();
      try { gfx.destroy(); } catch { /* gone */ }
      try { featureGfx?.destroy(); } catch { /* gone */ }
    },
  });
};

registerEffect({ kind: 'parallax', build: buildParallax, depth: Z_LAYERS.BACKGROUND, mooded: false });
